import { useEffect, useRef, useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    setStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d7d42tmm3fsfopf23lqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contacto" className="bg-[#F7F5F0] py-28 md:py-36">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.22em] uppercase text-[#C4553A] block mb-5">
            {t('contact_label')}
          </span>
          <h2 className="font-['Instrument_Serif'] text-4xl md:text-5xl text-[#0B0F1A] leading-tight mb-4">
            {t('contact_title')}
          </h2>
          <p className="font-['DM_Sans'] text-[#6B7280] text-base leading-relaxed max-w-md">
            {t('contact_sub')}
          </p>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-[#E8E4DC] mb-14" />

        {/* Form */}
        <div className="max-w-2xl">
          <div
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-7"
            >
              {/* Name */}
              <div className="flex flex-col gap-2">
                <label className="font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">
                  {t('contact_name')}
                </label>
                <input
                  type="text"
                  name="nombre"
                  required
                  placeholder={t('contact_name_ph')}
                  className="font-['DM_Sans'] text-sm text-[#0B0F1A] bg-white border border-[#E8E4DC] rounded-lg px-4 py-3 outline-none focus:border-[#0B0F1A] transition-colors duration-200 placeholder:text-[#6B7280]/50"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">
                  {t('contact_email')}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('contact_email_ph')}
                  className="font-['DM_Sans'] text-sm text-[#0B0F1A] bg-white border border-[#E8E4DC] rounded-lg px-4 py-3 outline-none focus:border-[#0B0F1A] transition-colors duration-200 placeholder:text-[#6B7280]/50"
                />
              </div>

              {/* Message */}
              <div className="flex flex-col gap-2">
                <label className="font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#6B7280] flex justify-between items-center">
                  <span>{t('contact_message')}</span>
                  <span className={`text-[10px] ${charCount > 450 ? 'text-[#C4553A]' : 'text-[#6B7280]/60'}`}>
                    {charCount}/500
                  </span>
                </label>
                <textarea
                  name="mensaje"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder={t('contact_message_ph')}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="font-['DM_Sans'] text-sm text-[#0B0F1A] bg-white border border-[#E8E4DC] rounded-lg px-4 py-3 outline-none focus:border-[#0B0F1A] transition-colors duration-200 placeholder:text-[#6B7280]/50 resize-none"
                />
              </div>

              {status === 'success' && (
                <p className="font-['DM_Sans'] text-sm text-[#1A3A5C] bg-[#1A3A5C]/10 border border-[#1A3A5C]/20 rounded-lg px-4 py-3">
                  {t('contact_success')}
                </p>
              )}
              {status === 'error' && (
                <p className="font-['DM_Sans'] text-sm text-[#C4553A] bg-[#C4553A]/10 border border-[#C4553A]/20 rounded-lg px-4 py-3">
                  {t('contact_error')}
                </p>
              )}

              <div>
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="font-['DM_Sans'] text-sm font-medium bg-[#C4553A] text-white px-8 py-3 rounded-md hover:bg-[#b04030] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {status === 'sending' ? t('contact_sending') : t('contact_send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
